if (!customElements.get('olyrel-painting-options')) {
  customElements.define('olyrel-painting-options', class extends HTMLElement {
    connectedCallback() {
      this.remembered = {};
      this.controller = new AbortController();
      this.addEventListener('click', event => {
        const button = event.target.closest('button[data-shape]');
        if (!button || !this.contains(button) || button.dataset.shape === this.shape) return;
        this.remembered[this.shape] = this.select.value;
        this.filterSizes(button.dataset.shape, this.remembered[button.dataset.shape]);
        // Use the theme's server-rendered variant selection, including variants beyond 250.
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
      }, { signal: this.controller.signal });
      this.syncPicker();
      this.observer = new MutationObserver(() => this.syncPicker());
      this.observer.observe(this, { childList: true, subtree: true });
    }
    syncPicker() {
      const picker = this.querySelector('variant-picker');
      if (!picker || picker === this.picker) return;
      this.picker = picker;
      this.select = picker.querySelector('select[name="options[Size]"]');
      if (!this.select) return;
      this.allSizes = Array.from(this.select.options, option => option.cloneNode(true));
      const selected = this.select.selectedOptions[0];
      if (!selected?.dataset.shape) return;
      this.filterSizes(selected.dataset.shape, selected.value);
      this.querySelector('.custom-painting-shapes').hidden = false;
    }
    filterSizes(shape, preferredValue) {
      const options = this.allSizes.filter(option => option.dataset.shape === shape);
      const selectedValue = options.find(option => option.value === preferredValue)?.value || options[0]?.value;
      if (!selectedValue) return;
      this.shape = shape;
      this.select.replaceChildren(...options.map(option => {
        const copy = option.cloneNode(true);
        copy.selected = copy.value === selectedValue;
        copy.toggleAttribute('selected', copy.selected);
        return copy;
      }));
      this.remembered[shape] = selectedValue;
      this.querySelectorAll('button[data-shape]').forEach(button =>
        button.setAttribute('aria-pressed', String(button.dataset.shape === shape)));
      this.dispatchEvent(new CustomEvent('olyrel:options-change', { bubbles: true }));
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
    }
  });
}

if (!customElements.get('olyrel-photo-upload')) {
  customElements.define('olyrel-photo-upload', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.file = this.querySelector('input[type="file"]');
      this.error = this.querySelector('[role="alert"]');
      this.preview = this.querySelector('img');
      this.dropzone = this.querySelector('.custom-photo-dropzone');
      this.selection = this.querySelector('.custom-photo-selection');
      this.status = this.querySelector('.custom-photo-status');
      const form = document.getElementById(this.dataset.form);
      if (!form) return;
      form.enctype = 'multipart/form-data';
      this.file.addEventListener('change', () => {
        this.validate();
        this.updatePreview();
      }, options);
      this.file.addEventListener('invalid', event => {
        event.preventDefault();
        this.validate();
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.file.focus({ preventScroll: true });
      }, options);
      this.querySelector('.custom-photo-remove').addEventListener('click', () => {
        this.file.value = '';
        this.file.setCustomValidity('');
        this.file.removeAttribute('aria-invalid');
        this.error.hidden = true;
        this.updatePreview();
        this.status.textContent = 'Photo removed. Please choose a photo before adding to cart.';
        this.file.focus({ preventScroll: true });
      }, options);
      for (const name of ['dragenter', 'dragover']) {
        this.dropzone.addEventListener(name, event => {
          event.preventDefault();
          this.dropzone.classList.add('is-dragging');
        }, options);
      }
      for (const name of ['dragleave', 'drop']) {
        this.dropzone.addEventListener(name, () => this.dropzone.classList.remove('is-dragging'), options);
      }
      this.dropzone.addEventListener('drop', event => {
        event.preventDefault();
        const dropped = event.dataTransfer?.files;
        if (!dropped?.length) return;
        if (dropped.length !== 1) {
          this.error.textContent = 'Please choose one photo at a time.';
          this.error.hidden = false;
          return;
        }
        this.file.files = dropped;
        this.file.dispatchEvent(new Event('change', { bubbles: true }));
      }, options);
      form.addEventListener('submit', event => {
        if (!this.validate()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.file.reportValidity();
        }
      }, { ...options, capture: true });
    }
    updatePreview() {
      if (this.previewURL) URL.revokeObjectURL(this.previewURL);
      this.previewURL = null;
      this.preview.removeAttribute('src');
      const file = this.file.files[0];
      const ready = Boolean(file && this.file.validity.valid);
      this.classList.toggle('has-photo', ready);
      this.selection.hidden = !ready;
      this.querySelector('[data-choose-text]').textContent = ready ? 'Choose a different photo' : 'Choose a photo';
      this.file.setAttribute('aria-label', ready ? 'Choose a different photo' : 'Choose a photo');
      if (ready) {
        this.previewURL = URL.createObjectURL(file);
        this.preview.src = this.previewURL;
        this.querySelector('.custom-photo-filename').textContent = file.name;
        this.querySelector('.custom-photo-filesize').textContent = file.size < 1024 * 1024
          ? `${Math.max(1, Math.round(file.size / 1024))} KB`
          : `${(file.size / (1024 * 1024)).toFixed(1)} MB`;
        this.status.textContent = 'Photo selected. It will be attached when you add your painting to cart.';
      } else {
        this.status.textContent = '';
      }
      this.dispatchEvent(new CustomEvent('olyrel:photo-change', { bubbles: true, detail: { url: this.previewURL } }));
    }
    validate() {
      const file = this.file.files[0];
      let message = '';
      if (!file) message = 'Please upload a photo for your custom painting.';
      else if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) message = 'Please choose a JPG, PNG or WebP image.';
      else if (file.size > 10 * 1024 * 1024) message = 'Please choose an image smaller than 10 MB.';
      this.file.setCustomValidity(message);
      this.file.setAttribute('aria-invalid', message ? 'true' : 'false');
      this.error.textContent = message;
      this.error.hidden = !message;
      return !message;
    }
    disconnectedCallback() {
      this.controller?.abort();
      if (this.previewURL) URL.revokeObjectURL(this.previewURL);
    }
  });
}
